/** Updated Lifecycle component with reversed text orientation */
/** @jsx jsx */
import { jsx } from "theme-ui"
import { useState } from "react"
import PropTypes from "prop-types"
import { motion, AnimatePresence } from "motion/react"
import { render } from "storyblok-rich-text-react-renderer"

import { ab } from "../theme"
import Link from "./link"

const Lifecycle = ({
  baseWidth = 600,
  baseHeight = 600,
  currentLanguage,
  innerText = "Sample Text",
  innerRadiusRatio = 0.25,
  iconRadiusRatio = 0.15,
  data = [],
}) => {
  const [hoveredSegment, setHoveredSegment] = useState(null)
  const [tooltipHovered, setTooltipHovered] = useState(false)

  let leaveTimeout

  const hoverOffset = 20
  const margin = hoverOffset + 2

  const width = baseWidth + margin * 2
  const height = baseHeight + margin * 2

  const cx = baseWidth / 2
  const cy = baseHeight / 2
  const radius = Math.min(baseWidth, baseHeight) / 2
  const innerRadius = radius * innerRadiusRatio

  const total = data.reduce((sum, d) => sum + d.value, 0)

  const convertTextToTspans = (text, x = 0, lineHeight = 20) =>
    text.split("\n").map((line, index) => (
      <tspan key={index} x={x} dy={index === 0 ? 0 : lineHeight}>
        {line}
      </tspan>
    ))

  const handleMouseEnter = (i, s, infoX, infoY) => {
    clearTimeout(leaveTimeout)
    setHoveredSegment({ index: i, data: s, infoX, infoY })
  }

  const handleMouseLeave = () => {
    clearTimeout(leaveTimeout)
    leaveTimeout = window.setTimeout(() => setHoveredSegment(null), 300)
  }

  const handleTooltipEnter = () => {
    clearTimeout(leaveTimeout)
    setTooltipHovered(true)
  }

  const handleTooltipLeave = () => {
    setTooltipHovered(false)
    leaveTimeout = window.setTimeout(() => {
      setHoveredSegment(null)
    }, 300)
  }

  // Prepare segments
  let cumulativeAngle = 0
  const segments = data.map((d) => {
    const angle = (d.value / total) * 2 * Math.PI
    const start = cumulativeAngle
    const end = cumulativeAngle + angle
    const mid = start + angle / 2

    // Segment outer/inner arc
    const x1o = cx + radius * Math.cos(start)
    const y1o = cy + radius * Math.sin(start)
    const x2o = cx + radius * Math.cos(end)
    const y2o = cy + radius * Math.sin(end)
    const x2i = cx + innerRadius * Math.cos(end)
    const y2i = cy + innerRadius * Math.sin(end)
    const x1i = cx + innerRadius * Math.cos(start)
    const y1i = cy + innerRadius * Math.sin(start)
    const large = angle > Math.PI ? 1 : 0
    const path = `M ${x1o} ${y1o} A ${radius} ${radius} 0 ${large} 1 ${x2o} ${y2o} L ${x2i} ${y2i} L ${x1i} ${y1i} Z`

    let textR
    let txStart
    let tyStart
    let txEnd
    let tyEnd
    let textPath

    // Reversed text path (end->start, sweep=0)
    if (d.flipText) {
      textR = radius - 47
      txStart = cx + textR * Math.cos(end)
      tyStart = cy + textR * Math.sin(end)
      txEnd = cx + textR * Math.cos(start)
      tyEnd = cy + textR * Math.sin(start)
      textPath = `M ${txStart} ${tyStart} A ${textR} ${textR} 0 ${large} 0 ${txEnd} ${tyEnd}`
    } else {
      textR = radius - 40
      txStart = cx + textR * Math.cos(start)
      tyStart = cy + textR * Math.sin(start)
      txEnd = cx + textR * Math.cos(end)
      tyEnd = cy + textR * Math.sin(end)
      textPath = `M ${txStart} ${tyStart} A ${textR} ${textR} 0 ${large} 1 ${txEnd} ${tyEnd}`
    }

    // Icon coordinates
    const iconR = innerRadius + (radius - innerRadius) * iconRadiusRatio
    const iconX = cx + iconR * Math.cos(mid)
    const iconY = cy + iconR * Math.sin(mid)

    cumulativeAngle += angle
    return { ...d, path, textPath, mid, iconX, iconY, start, end }
  })

  // Inner polygon
  cumulativeAngle = 0
  const innerPoints = data.map((d) => {
    const ang = (d.value / total) * 2 * Math.PI
    const px = cx + innerRadius * Math.cos(cumulativeAngle)
    const py = cy + innerRadius * Math.sin(cumulativeAngle)
    cumulativeAngle += ang
    return `${px},${py}`
  })

  return (
    <div
      sx={{
        maxWidth: ab({ md: "60%" }),
        margin: "0 auto",
        overflow: "visible",
        position: "relative",
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height="auto"
        preserveAspectRatio="xMidYMid meet"
      >
        <g transform={`translate(${margin},${margin})`}>
          <defs>
            {segments.map((s, i) => (
              <path key={i} id={`text${i}`} d={s.textPath} fill="none" />
            ))}
          </defs>

          {segments.map((s, i) => {
            const dx = hoverOffset * Math.cos(s.mid)
            const dy = hoverOffset * Math.sin(s.mid)
            const angle = s.mid

            // Tooltip positioning
            const outsideR = radius + hoverOffset + 10
            const bx = cx + outsideR * Math.cos(angle)
            const by = cy + outsideR * Math.sin(angle)
            const boxW = 200
            const boxH = 200
            let xOff = 0
            let yOff = 0
            if (Math.sin(angle) > 0.1) yOff = -boxH - 10
            else if (Math.sin(angle) < -0.1) yOff = 10
            if (Math.cos(angle) > 0.1) xOff = 10
            else if (Math.cos(angle) < -0.1) xOff = -boxW - 10
            const infoX = bx + xOff
            const infoY = by + yOff

            return (
              <Link key={i} to={s.url} language={currentLanguage}>
                <motion.g
                  initial={{ x: 0, y: 0 }}
                  whileHover={{ x: dx, y: dy }}
                  transition={{ type: "spring", stiffness: 200, damping: 20 }}
                  onMouseEnter={() => {
                    // Calculate tooltip position based on angle (s.mid in radians)
                    let tooltipPosition = "right"
                    const deg = (s.mid * 180) / Math.PI
                    if (deg >= 45 && deg < 135) {
                      tooltipPosition = "bottom"
                    } else if (deg >= 135 && deg < 225) {
                      tooltipPosition = "left"
                    } else if (deg >= 225 && deg < 315) {
                      tooltipPosition = "top"
                    }
                    handleMouseEnter(i, { ...s, tooltipPosition }, infoX, infoY)
                  }}
                  onMouseLeave={handleMouseLeave}
                >
                  <path
                    d={s.path}
                    style={{ fill: s.color, stroke: "#fff", strokeWidth: 10 }}
                  />
                  <path
                    d={`M ${cx} ${cy} L ${cx + (radius / 2) * Math.cos(s.start)} ${
                      cy + (radius / 2) * Math.sin(s.start)
                    } A ${radius / 2} ${radius / 2} 0 ${s.end - s.start > Math.PI ? 1 : 0} 1 ${
                      cx + (radius / 2) * Math.cos(s.end)
                    } ${cy + (radius / 2) * Math.sin(s.end)} Z`}
                    style={{ fill: "rgba(255,255,255,0.3)" }}
                  />
                  <text
                    style={{
                      fontSize: "1em",
                      fontWeight: "bold",
                      fill: "#fff",
                      textTransform: "uppercase",
                    }}
                  >
                    <textPath
                      href={`#text${i}`}
                      startOffset="50%"
                      textAnchor="middle"
                      method="align"
                    >
                      {convertTextToTspans(s.label)}
                    </textPath>
                  </text>
                  {(() => {
                    const midDeg =
                      (s.mid * 180) / Math.PI + 90 + s.flipText * 180
                    const iconSize = 40
                    return (
                      <image
                        href={s.icon}
                        x={s.iconX - iconSize / 2}
                        y={s.iconY - iconSize / 2}
                        width={iconSize}
                        height={iconSize}
                        transform={`rotate(${midDeg}, ${s.iconX}, ${s.iconY})`}
                      />
                    )
                  })()}
                </motion.g>
              </Link>
            )
          })}

          <polygon
            points={innerPoints.join(",")}
            style={{ fill: "#fff", stroke: "#fff", strokeWidth: 2 }}
          />
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dy="0.3em"
            style={{
              fontSize: "1em",
              fill: "#333",
              textTransform: "uppercase",
            }}
          >
            {convertTextToTspans(innerText, cx)}
          </text>
        </g>
      </svg>

      <AnimatePresence>
        {hoveredSegment && hoveredSegment.data.extraInfo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            xmlns="http://www.w3.org/1999/xhtml"
            className="tooltip"
            style={{
              position: "absolute",
              top:
                hoveredSegment.data.tooltipPosition === "top"
                  ? "-40%"
                  : hoveredSegment.data.tooltipPosition === "bottom"
                    ? "100%"
                    : hoveredSegment.data.extraInfoYPos,
              left:
                hoveredSegment.data.tooltipPosition === "left"
                  ? "-25%"
                  : hoveredSegment.data.extraInfoXPos,
              background: hoveredSegment.data.color,
              color: "#fff",
              padding: 10,
              fontSize: "0.9em",
              width: 200,
              height: 300,
              overflowY: "auto",
            }}
            sx={{
              display: ab({ _: "none", md: "none", lg: "block" }),
              zIndex: 1000,
              a: {
                color: "#fff",
                textDecoration: "underline",
              },
            }}
            onMouseEnter={handleTooltipEnter}
            onMouseLeave={handleTooltipLeave}
          >
            <strong sx={{ textTransform: "uppercase" }}>
              {hoveredSegment.data.label}
            </strong>
            <div>{render(hoveredSegment.data.extraInfo)}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

Lifecycle.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      extraInfo: PropTypes.object,
    })
  ),
  baseWidth: PropTypes.number,
  baseHeight: PropTypes.number,
  currentLanguage: PropTypes.string.isRequired,
  innerText: PropTypes.string,
  innerRadiusRatio: PropTypes.number,
  iconRadiusRatio: PropTypes.number,
}

export default Lifecycle
